<style lang="scss" scoped>
.dialog-partner-info {
  display: flex;
  padding: 10px;
  position: relative;
  flex-flow: column;
  align-items: center;
  height: calc(100% - 33px);
}

.dialog-partner-image {
  height: 120px;
  width: 120px;
}

.dialog-partner__name-wrapper {
  display: flex;
  align-items: center;
  max-width: 100%;
  margin: 10px;
}

.dialog-partner-name {
  color: $gray-900;
}

.dialog-partner-info__right {
  display: flex;
  flex-flow: column;
  justify-content: flex-end;
}

.friend-request-button {
  position: absolute;
  right: 10px;
  top: 10px;
  color: #6c757d;
  transition: color 0.3s, transform 0.3s;
  padding: 0;
  background-color: transparent;

  &:hover {
    cursor: pointer;
    color: $gray-900;
    transform: scale(1.2);
  }

  &:focus {
    box-shadow: none;
  }
}

@media (min-width: 768px) {
  .dialog-partner-info {
    flex-flow: row;
    align-items: stretch;
    height: 80px;
  }

  .dialog-partner-image {
    margin-right: 0.5rem;
    width: 60px;
    height: 60px;
  }

  .dialog-partner__name-wrapper {
    max-width: calc(100% - 300px);
    margin: 0;
  }

  .dialog-partner-info__right {
    max-width: 220px;
  }
}
</style>

<template>
  <div class="dialog-partner-info border-bottom" v-if="!loading">
    <img
      v-if="!isDeleted"
      class="dialog-partner-image rounded-circle"
      :src="imageUrl"
    />
    <div
      v-else
      class="dialog-partner-image rounded-circle mr-2 bg-light d-flex align-items-center justify-content-center"
    >
      <i class="far fa-times-circle"></i>
    </div>
    <div class="dialog-partner__name-wrapper text-truncate mr-3">
      <a :href="isSupport ? null : profileUrl" class="text-truncate">
        <strong class="text-truncate dialog-partner-name">{{ getName }}</strong>
      </a>
      <online-circle
        v-if="!isSupport && !isDeleted"
        class="ml-2"
        :id="partner.id"
      ></online-circle>
    </div>
    <div v-if="!isSupport && !isDeleted" class="dialog-partner-info__right">
      <div>{{ partner.age }} Jahre, aus {{ partner.anonymous_zipcode }}</div>
      <div>{{ imagesText }}, {{ audioText }}</div>
    </div>
    <v-button
      v-if="!isSupport && !isDeleted"
      class="friend-request-button"
      :working="sendingFriendRequest"
      @click.native="handleFriendRequest"
      v-tooltip:left="friendRequestText"
    >
      <i class="fas" :class="friendRequestClass"></i>
    </v-button>
  </div>
</template>

<script lang="babel">
import OnlineCircle from "@/components/OnlineCircle";
import FriendshipMixin from "@/components/Customer/mixins/Friendship";
import swal from "sweetalert2";

export default {
  mixins: [FriendshipMixin],
  components: {
    OnlineCircle
  },
  data() {
    return {
      loading: true,
      partner: null,
    };
  },
  created() {
    this.load();
  },
  props: {
    id: {
      type: Number,
      required: true,
    }
  },
  computed: {
    otherId() {
      return this.partner.id;
    },
    imageUrl() {
      return `${this.partner.primary_image.url}?width=120&height=120`;
    },
    isSupport() {
      return this.partner && this.partner.type === 2;
    },
    images() {
      return this.partner.media.filter(media => media.is_image);
    },
    audio() {
      return this.partner.media.filter(media => media.is_audio);
    },
    imagesText() {
      if (this.partner.media.length === 1) {
        return "1 Bild";
      }

      return this.partner.media.length + " Bilder";
    },
    audioText() {
      if (this.audio.length === 1) {
        return "1 Sprachnachricht";
      }

      return this.audio.length + " Sprachnachrichten";
    },
    profileUrl() {
      return this.partner ? "/mitglied/"+this.partner.name : null;
    },
    friendRequestClass() {
      if (this.areFriends) {
        return "fa-user-times text-danger";
      }

      if (this.friendRequestSent || this.friendRequestReceived) {
        return "fa-user-plus text-warning";
      }

      return "fa-user-plus";
    },
    friendRequestText() {
      if (this.areFriends) {
        return "Freundschaft beenden?";
      }

      if (this.friendRequestSent || this.friendRequestReceived) {
        return "Freundschaftsanfrage offen";
      }

      return `Freundschaftsanfrage senden für ${Laravel.settings.pricePerFriendRequest} ${Laravel.lang.currency}`;
    },
    isDeleted() {
      return this.partner === undefined;
    },
    getName() {
      return this.partner ? this.partner.name : 'Gelöscht';
    }
  },
  methods: {
    handleFriendRequest() {
      if (this.areFriends) {
        swal.fire({
          title: "Freundschaft beenden?",
          text: `Willst du wirklich die Freundschaft mit ${this.partner.name} beenden?`,
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#28a745",
          cancelButtonColor: "#d33",
          confirmButtonText: "Ja",
          cancelButtonText: "Nein"
        }).then((result) => {
          if (result.value) {
            this.sendingFriendRequest = true;
            this.$http.delete(`/api/v1/friends/${this.friendRequest.id}`).then(() => {
              location.reload();
            }).finally(() => {
              this.sendingFriendRequest = false;
            });
          }
        });
      } else {
        this.sendFriendRequest();
      }
    },
    load() {
      this.loading = true;
      this.$http.get("/api/v1/customerProfile", {
        params: {
          filter: [
            {
              name: "where",
              attributes: {
                id: this.id,
              }
            },
            {
              name: "with",
              value: "media",
            }
          ]
        }
      })
      .then(({data}) => {
        this.partner = data[0];
        this.loading = false;
      })
      .catch(() => {
        this.$notify({
          title: "Ein Fehler ist aufgetreten bitte probier es nocheinmal",
          type: "error"
        });
      });
    }
  },
  watch: {
    id() {
      this.load();
    }
  }
}
</script>
